import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import CurrencyTextField from './CurrencyTextField';
import { serviceService } from '../services/serviceService';

const emptyServiceForm = () => ({
  useCustomServiceCode: false,
  service_code: '',
  description: '',
  quantity: '1',
  unit_price: '',
  unit: 'HR'
});

const NewServiceModal = ({ open, onClose, onCreated }) => {
  const [formData, setFormData] = useState(emptyServiceForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    if (!saving) {
      setFormData(emptyServiceForm());
      setError('');
      onClose();
    }
  };

  const handleCreate = async () => {
    const { useCustomServiceCode, service_code, description } = formData;
    if (!description?.trim()) {
      setError('Description is required.');
      return;
    }
    if (useCustomServiceCode && !service_code?.trim()) {
      setError('Service code is required when using a custom service code.');
      return;
    }
    if (useCustomServiceCode && service_code?.trim() && !/^[A-Za-z0-9-]+$/.test(service_code.trim())) {
      setError('Service code may contain only letters, numbers, and dashes.');
      return;
    }
    const rawQty =
      formData.quantity != null && String(formData.quantity).trim() !== ''
        ? parseFloat(String(formData.quantity).replace(/,/g, ''))
        : NaN;
    if (!Number.isFinite(rawQty) || rawQty <= 0) {
      setError('Quantity must be a positive number.');
      return;
    }
    const qty = Math.round(rawQty * 100) / 100;
    if (qty <= 0) {
      setError('Quantity must be a positive number.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload = {
        description: description.trim(),
        unit: formData.unit?.trim() || 'HR',
        unit_price: formData.unit_price ? parseFloat(formData.unit_price) : 0
      };
      if (useCustomServiceCode && service_code?.trim()) {
        payload.service_code = service_code.trim();
      }
      const res = await serviceService.create(payload);
      const createdService = res?.data ?? res;
      handleClose();
      const lineItemFields = {
        description: formData.description?.trim() ?? '',
        quantity: qty
      };
      onCreated?.(createdService, lineItemFields);
    } catch (err) {
      console.error('Error creating service:', err);
      const msg = err.response?.data?.message || 'Failed to create service';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Service</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.useCustomServiceCode}
                  onChange={(e) => setFormData((f) => ({ ...f, useCustomServiceCode: e.target.checked }))}
                  size="small"
                />
              }
              label="Use custom service code"
            />
          </Grid>
          {formData.useCustomServiceCode ? (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Service Code"
                value={formData.service_code}
                onChange={(e) => setFormData((f) => ({ ...f, service_code: e.target.value }))}
                placeholder="e.g. LAB-001"
                helperText="Letters, numbers, and dashes only"
              />
            </Grid>
          ) : (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Service Code"
                value="Auto / optional"
                disabled
                helperText="Leave blank for no code, or enable custom code above"
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Description"
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
              placeholder="Service description (required)"
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              inputProps={{ min: 0, step: 'any' }}
              label="Quantity"
              value={formData.quantity}
              onChange={(e) => setFormData((f) => ({ ...f, quantity: e.target.value }))}
              placeholder="1"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Unit"
              value={formData.unit}
              onChange={(e) => setFormData((f) => ({ ...f, unit: e.target.value }))}
              placeholder="HR"
            />
          </Grid>
          <Grid item xs={12}>
            <CurrencyTextField
              fullWidth
              size="small"
              label="Unit Price"
              value={formData.unit_price}
              onChange={(e) => setFormData((f) => ({ ...f, unit_price: e.target.value }))}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleCreate} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewServiceModal;
